import { Spinner } from "@/components/ui/spinner/spinner";
import { useEntityDocuments } from "@/queries/documents/use-entity-documents";
import { FileTextIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover/popover";
import { QueryError } from "@/components/ui/query-error/query-error";
import { useTranslation } from "react-i18next";

export function BomDocument({ materialId }: { materialId: string }) {
  const { t } = useTranslation();
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const iconRef = useRef<HTMLSpanElement | null>(null);
  const [isClicked, setIsClicked] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const navigate = useNavigate();
  const { documents, refetch, isLoading, isStale, error } = useEntityDocuments({
    entityId: materialId,
    passive: true,
  });

  const handleDocumentNavigation = useCallback(() => {
    if (!documents?.length) return;

    if (documents.length === 1) {
      navigate(ROUTES.DOCUMENTS(documents[0].Id!));
    } else {
      setPopoverOpen(true);
    }

    setIsClicked(false);
  }, [documents, navigate]);

  const handleIconClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setIsClicked(true);

      if (popoverOpen) {
        setPopoverOpen(false);
      } else if (!documents || isStale) {
        refetch();
      } else {
        handleDocumentNavigation();
      }
    },
    [documents, handleDocumentNavigation, isStale, popoverOpen, refetch]
  );

  const handleDocClick = useCallback(() => setPopoverOpen(false), []);

  useEffect(() => {
    if (error) {
      setPopoverOpen(true);
      return;
    }

    if (isClicked) {
      handleDocumentNavigation();
    }

    return () => {
      setPopoverOpen(false);
    };
  }, [documents, error]);

  return (
    <div ref={setRef} className="popover-absolute">
      <Popover open={popoverOpen}>
        <PopoverAnchor>
          <span
            ref={iconRef}
            className="flex justify-end items-center cursor-pointer"
            aria-label="bom-item document button"
            onClick={handleIconClick}
          >
            {isLoading ? (
              <Spinner className="mr-0 w-5 h-5" />
            ) : (
              <FileTextIcon size={22} />
            )}
          </span>
        </PopoverAnchor>
        <PopoverContent
          container={ref}
          sideOffset={16}
          className="flex flex-col w-auto gap-2 max-w-2xl cursor-default font-normal"
          onClick={(e) => e.stopPropagation()}
          onEscapeKeyDown={() => setPopoverOpen(false)}
          onPointerDownOutside={(e) => {
            const isIconClick = iconRef.current?.contains(e.target as Node);
            if (isIconClick) return;

            setPopoverOpen(false);
          }}
        >
          {error && (
            <QueryError
              resource={t("documentViewer.document")}
              errors={[error]}
              retries={[refetch]}
            />
          )}
          {documents?.map(
            (doc) =>
              doc.Id && (
                <Link
                  key={doc.Id}
                  to={ROUTES.DOCUMENTS(doc.Id)}
                  onClick={handleDocClick}
                  className="flex items-center gap-10 justify-between p-2 border rounded-sm"
                >
                  <p className="text-nowrap overflow-hidden overflow-ellipsis">
                    {doc.FileName}
                  </p>
                  <span>
                    <FileTextIcon size={18} className="cursor-pointer" />
                  </span>
                </Link>
              )
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
